import type { EndpointDTO } from '@devrobot/shared';
import { CircleCheck, CircleDashed, ExternalLink, Globe } from 'lucide-react';
import { CopyButton } from '../../components/CopyButton';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { httpsReadiness } from '../../lib/endpoints';

function EndpointLink({ e }: { e: EndpointDTO }) {
  return (
    <>
      <a href={e.url} target="_blank" rel="noreferrer" className="link link-hover inline-flex items-center gap-1 font-mono text-sm font-medium break-anywhere">
        {e.url} <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
      </a>
      <div className="text-xs text-base-content/55">{e.kind === 'route' ? 'hostname via managed proxy' : `host port ${e.hostPort}`}</div>
    </>
  );
}

function HttpsStatus({ e }: { e: EndpointDTO }) {
  const https = httpsReadiness(e);
  return https.ready ? (
    <span className="badge badge-soft badge-success badge-sm gap-1">
      <CircleCheck className="size-3" aria-hidden="true" /> HTTPS ready
    </span>
  ) : (
    <span className="text-xs text-base-content/65">
      <span className="badge badge-ghost badge-sm mr-1 gap-1">
        <CircleDashed className="size-3" aria-hidden="true" /> not ready
      </span>
      {https.reason}
    </span>
  );
}

function DnsRecords({ e }: { e: EndpointDTO }) {
  if (e.kind !== 'route' || e.dns === 'ok' || e.dnsRecords.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1">
      <p className="text-xs text-base-content/65">Create at your DNS provider:</p>
      {e.dnsRecords.map((r, i) => (
        <div key={i} className="flex items-center gap-1 rounded-field bg-base-200/60 py-0.5 pr-0.5 pl-2 font-mono text-xs">
          <span className="min-w-0 flex-1 break-anywhere">
            {r.type} {r.name} → {r.value}
          </span>
          <CopyButton value={`${r.name} ${r.type} ${r.value}`} label={`Copy ${r.type} record for ${r.name}`} />
        </div>
      ))}
    </div>
  );
}

function CertificateError({ e }: { e: EndpointDTO }) {
  return e.certificateError ? <p className="mt-1 max-w-xs text-xs break-anywhere dr-ink-error">{e.certificateError}</p> : null;
}

export function EndpointsTable({ endpoints, stale }: { endpoints: EndpointDTO[]; stale?: boolean }) {
  if (endpoints.length === 0) {
    return (
      <EmptyState icon={Globe} title="No public endpoints">
        Nothing is exposed outside the server. Add a hostname or host port in the project’s Deploy tab and deploy again.
      </EmptyState>
    );
  }
  return (
    <>
      {/* Phones: stacked rows (endpoint, service, then the readiness chain) instead of a five-column table. */}
      <ul className="-mx-1 divide-y divide-hairline md:hidden">
        {endpoints.map((e) => (
          <li key={e.url} className="space-y-2 px-1 py-3 first:pt-0 last:pb-0">
            <div className="min-w-0">
              <EndpointLink e={e} />
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="font-mono text-base-content/65">
                {e.service}:{e.containerPort}
              </span>
              {e.kind === 'route' ? (
                <>
                  <StatusBadge kind="dns" value={e.dns} size="xs" />
                  <StatusBadge kind="cert" value={e.certificate} stale={stale} size="xs" />
                </>
              ) : null}
            </div>
            <DnsRecords e={e} />
            <CertificateError e={e} />
            <HttpsStatus e={e} />
          </li>
        ))}
      </ul>
      <div className="-mx-1 overflow-x-auto max-md:hidden">
        <table className="table table-sm">
          <thead>
            <tr className="text-xs tracking-wide text-base-content/50 uppercase">
              <th>Endpoint</th>
              <th>Service</th>
              <th>DNS</th>
              <th>Certificate</th>
              <th>HTTPS</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((e) => (
              <tr key={e.url} className="align-top hover:bg-base-200/50">
                <td className="min-w-48">
                  <EndpointLink e={e} />
                </td>
                <td className="font-mono text-xs">
                  {e.service}:{e.containerPort}
                </td>
                <td className="min-w-40">
                  {e.kind === 'route' ? <StatusBadge kind="dns" value={e.dns} size="xs" /> : <span className="text-xs text-base-content/50">—</span>}
                  <DnsRecords e={e} />
                </td>
                <td className="min-w-32">
                  {e.kind === 'route' ? <StatusBadge kind="cert" value={e.certificate} stale={stale} size="xs" /> : <span className="text-xs text-base-content/50">—</span>}
                  <CertificateError e={e} />
                </td>
                <td>
                  <HttpsStatus e={e} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
